import type { StepHandler } from '.';
import { logger } from '@php-wasm/logger';
/**
 * @inheritDoc activatePlugin
 * @example
 *
 * <code>
 * {
 * 		"step": "activatePlugin",
 * 		"pluginName": "Gutenberg",
 * 		"pluginPath": "/wordpress/wp-content/plugins/gutenberg"
 * }
 * </code>
 */
export interface ActivatePluginStep {
	step: 'activatePlugin';
	/**
	 * Path to the plugin directory as absolute path
	 * (/wordpress/wp-content/plugins/plugin-name); or the plugin entry file
	 * relative to the plugins directory (plugin-name/plugin-name.php).
	 */
	pluginPath: string;
	/** Optional. Plugin name to display in the progress bar. */
	pluginName?: string;
}

/**
 * Activates a WordPress plugin (if it's installed).
 *
 * @param playground The playground client.
 */
export const activatePlugin: StepHandler<ActivatePluginStep> = async (
	playground,
	{ pluginPath, pluginName },
	progress
) => {
	progress?.tracker.setCaption(`Activating ${pluginName || pluginPath}`);

	const docroot = await playground.documentRoot;
	/**
	 * Instead of checking the plugin activation response,
	 * check if the plugin is active by looking at the active plugins list.
	 *
	 * We have to split the activation and the check into two PHP runs
	 * because some plugins might redirect during activation,
	 * which would prevent any output that happens after activation from being returned.
	 *
	 * Relying on the plugin activation response is not reliable because if the plugin activation
	 * produces any output, WordPress will assume it's an activation error and return a WP_Error.
	 * WordPress will still activate the plugin and load the required page,
	 * but it will also show the error as a notice in wp-admin.
	 * See WordPress source code for more details:
	 * https://github.com/WordPress/wordpress-develop/blob/6.7/src/wp-admin/includes/plugin.php#L733
	 */
	const activatePluginResult = await playground.run({
		code: `<?php
			define( 'WP_ADMIN', true );
			require_once( getenv('DOCROOT') . "/wp-load.php" );
			require_once( getenv('DOCROOT') . "/wp-admin/includes/plugin.php" );

			// Set current user to admin
			wp_set_current_user( get_users(array('role' => 'Administrator') )[0]->ID );

			$plugin_path = getenv('PLUGIN_PATH');
			$response = false;
			if ( ! is_dir( $plugin_path)) {
				$response = activate_plugin($plugin_path);
			}

			// Activate plugin by name if activation by path wasn't successful
			if ( null !== $response ) {
				foreach ( ( glob( $plugin_path . '/*.php' ) ?: array() ) as $file ) {
					$info = get_plugin_data( $file, false, false );
					if ( ! empty( $info['Name'] ) ) {
						$response = activate_plugin( $file );
						break;
					}
				}
			}

			if ( is_wp_error($response) ) {
				die( $response->get_error_message() );
			} else if ( false === $response ) {
				die( "The activatePlugin step wasn't able to find the plugin $plugin_path." );
			}
		`,
		env: {
			PLUGIN_PATH: pluginPath,
			DOCROOT: docroot,
		},
	});
	if (activatePluginResult.text) {
		logger.warn(
			`Plugin ${pluginPath} activation printed the following bytes: ${activatePluginResult.text}`
		);
	}

	/**
	 * Instead of trusting the activation response, check the active plugins list.
	 *
	 * We try to discard any extra output via output buffering. The output of the script below
	 * end with `{"success": true}` or `{"success": false}`. Only `{"success": true}` is
	 * treated as a successful plugin activation.
	 */
	const activationStatusResult = await playground.run({
		code: `<?php
			ob_start();
			require_once( getenv( 'DOCROOT' ) . "/wp-load.php" );

			$plugin_directory = rtrim( WP_PLUGIN_DIR, '/' ) . '/';
			$relative_plugin_path = getenv( 'PLUGIN_PATH' );
			if (strpos($relative_plugin_path, $plugin_directory) === 0) {
				$relative_plugin_path = substr($relative_plugin_path, strlen($plugin_directory));
			}

			if ( is_dir( $plugin_directory . $relative_plugin_path ) ) {
				$relative_plugin_path = rtrim( $relative_plugin_path, '/' ) . '/';
			}

			$active_plugins = get_option( 'active_plugins' );
			if ( ! is_array( $active_plugins ) ) {
				$active_plugins = array();
			}
			ob_end_clean();

			/**
			 * Use a shutdown function to ensure the activation-related output comes
			 * last in stdout.
			 */
			register_shutdown_function( function() use ( $relative_plugin_path, $active_plugins ) {
				foreach ( $active_plugins as $plugin ) {
					if ( substr( $plugin, 0, strlen( $relative_plugin_path ) ) === $relative_plugin_path ) {
						die('{"success": true}');
						break;
					}
				}
				die('{"success": false}');
			});
		`,
		env: {
			DOCROOT: docroot,
			PLUGIN_PATH: pluginPath,
		},
	});

	const rawStatus = (activationStatusResult.text ?? '').trim();
	if (rawStatus.endsWith('{"success": true}')) {
		return;
	}
	if (rawStatus !== '{"success": false}') {
		logger.debug(rawStatus);
	}

	throw new Error(
		`Plugin ${pluginPath} could not be activated - WordPress exited with exit code ${activatePluginResult.exitCode}. ` +
			`Inspect the "debug" logs in the console for more details. Output headers: ${JSON.stringify(
				activatePluginResult.headers,
				null,
				2
			)}`
	);
};
